import { batchId, normalizeSerial, serialId, type SyncOp } from '@ims/core'
import {
  batches,
  brands,
  categories,
  customers,
  diningAreas,
  diningTables,
  goodsReceiptLines,
  goodsReceipts,
  invoicePrints,
  locations,
  productCodes,
  productComponents,
  productSerials,
  products,
  stockMovements,
  suppliers,
  tablesByName,
  taxRates,
  units,
  type Row,
  type TableName,
  type WritableRow,
} from '@ims/data'

// Factories for new rows. Typed against the registry's WritableRow, so adding
// a column to a migration + registry makes these fail to compile until the
// new column gets a default here.

export interface RowContext {
  tenantId: string
  now?: string
}

function base(ctx: RowContext) {
  const now = ctx.now ?? new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    tenant_id: ctx.tenantId,
    created_at: now,
    updated_at: now,
    deleted_at: null,
  }
}

export function newProduct(
  ctx: RowContext,
  fields: Partial<WritableRow<typeof products>> & { sku: string; name: string },
): WritableRow<typeof products> {
  return {
    ...base(ctx),
    barcode: null,
    description: null,
    category_id: null,
    unit_id: null,
    tax_rate_id: null,
    supplier_id: null,
    sell_price_paisa: 0,
    cost_price_paisa: null,
    price_includes_tax: false,
    track_stock: true,
    min_stock_milli: 0,
    reorder_point_milli: 0,
    image_path: null,
    is_active: true,
    brand_id: null,
    manufacturer: null,
    mrp_paisa: null,
    hs_code: null,
    shelf_location: null,
    tags: null,
    reorder_qty_milli: 0,
    attributes: null,
    track_batches: false,
    track_expiry: false,
    shelf_life_days: null,
    shelf_life_unit: null,
    kind: 'standard',
    parent_id: null,
    variant_options: null,
    variant_values: null,
    track_serials: false,
    warranty_months: null,
    wholesale_price_paisa: null,
    min_price_paisa: null,
    plu_code: null,
    kitchen_station: 'kitchen',
    for_sale: true,
    diet: null,
    spicy: false,
    sold_out_until: null,
    ...fields,
  }
}

export function newBrand(ctx: RowContext, fields: { name: string }): WritableRow<typeof brands> {
  return { ...base(ctx), ...fields }
}

/** An extra barcode or pack size ("Box of 12" = 12,000 milli-units). */
export function newProductCode(
  ctx: RowContext,
  fields: Pick<WritableRow<typeof productCodes>, 'product_id' | 'code'> &
    Partial<WritableRow<typeof productCodes>>,
): WritableRow<typeof productCodes> {
  return { ...base(ctx), label: null, qty_milli: 1000, sell_price_paisa: null, ...fields }
}

export function newProductComponent(
  ctx: RowContext,
  fields: { product_id: string; component_id: string; qty_milli: number },
): WritableRow<typeof productComponents> {
  return { ...base(ctx), ...fields }
}

export function newCustomer(
  ctx: RowContext,
  fields: Partial<WritableRow<typeof customers>> & { name: string },
): WritableRow<typeof customers> {
  return {
    ...base(ctx),
    contact_name: null,
    email: null,
    phone: null,
    address: null,
    pan_vat_number: null,
    credit_limit_paisa: null,
    notes: null,
    is_active: true,
    price_tier: 'retail',
    ...fields,
  }
}

export function newSupplier(
  ctx: RowContext,
  fields: Partial<WritableRow<typeof suppliers>> & { name: string },
): WritableRow<typeof suppliers> {
  return {
    ...base(ctx),
    contact_name: null,
    email: null,
    phone: null,
    address: null,
    pan_vat_number: null,
    notes: null,
    is_active: true,
    ...fields,
  }
}

export function newLocation(
  ctx: RowContext,
  fields: Partial<WritableRow<typeof locations>> & { name: string },
): WritableRow<typeof locations> {
  return { ...base(ctx), type: 'store', address: null, is_active: true, ...fields }
}

export function newTaxRate(
  ctx: RowContext,
  fields: { name: string; rate_bp: number },
): WritableRow<typeof taxRates> {
  return { ...base(ctx), is_active: true, ...fields }
}

export function newUnit(
  ctx: RowContext,
  fields: { name: string; symbol: string; allows_decimal: boolean },
): WritableRow<typeof units> {
  return { ...base(ctx), ...fields }
}

export function newCategory(
  ctx: RowContext,
  fields: { name: string; parent_id?: string | null; sort_order?: number },
): WritableRow<typeof categories> {
  return { ...base(ctx), parent_id: null, sort_order: 0, ...fields }
}

export function newDiningArea(
  ctx: RowContext,
  fields: { name: string; sort_order?: number },
): WritableRow<typeof diningAreas> {
  return { ...base(ctx), sort_order: 0, ...fields }
}

export function newDiningTable(
  ctx: RowContext,
  fields: { name: string; area_id?: string | null; seats?: number | null; sort_order?: number },
): WritableRow<typeof diningTables> {
  return { ...base(ctx), area_id: null, seats: null, sort_order: 0, is_active: true, ...fields }
}

export function newMovement(
  ctx: RowContext,
  fields: Pick<
    WritableRow<typeof stockMovements>,
    'product_id' | 'location_id' | 'type' | 'qty_milli'
  > &
    Partial<WritableRow<typeof stockMovements>>,
): WritableRow<typeof stockMovements> {
  const row = base(ctx)
  return {
    ...row,
    unit_cost_paisa: null,
    ref_type: 'manual',
    ref_id: null,
    notes: null,
    occurred_at: row.created_at,
    batch_id: null,
    ...fields,
  }
}

/** What someone typed about a batch (on receiving, opening stock or import). */
export interface BatchInput {
  batchNo: string
  mfgDate?: string | null
  expiryDate?: string | null
  mrpPaisa?: number | null
  costPaisa?: number | null
  supplierId?: string | null
}

/**
 * The upsert for a product's batch (deterministic id, see core batchId), merged
 * into the batch as it exists: empty inputs never clear what is stored. Null
 * when nothing would change.
 */
export function batchOp(
  ctx: RowContext,
  productId: string,
  input: BatchInput,
  existing: Row<typeof batches> | null,
): { id: string; op: SyncOp | null } {
  const id = batchId(ctx.tenantId, productId, input.batchNo)
  const pick = <T>(value: T | null | undefined, stored: T | null | undefined): T | null =>
    value ?? stored ?? null
  const row: WritableRow<typeof batches> = {
    ...base(ctx),
    ...(existing ? { created_at: existing.created_at } : {}),
    id,
    product_id: productId,
    batch_no: existing?.batch_no ?? input.batchNo.trim(),
    mfg_date: pick(input.mfgDate, existing?.mfg_date),
    expiry_date: pick(input.expiryDate, existing?.expiry_date),
    mrp_paisa: pick(input.mrpPaisa, existing?.mrp_paisa),
    cost_paisa: pick(input.costPaisa, existing?.cost_paisa),
    supplier_id: pick(input.supplierId, existing?.supplier_id),
    notes: existing?.notes ?? null,
  }
  if (
    existing &&
    !existing.deleted_at &&
    (['mfg_date', 'expiry_date', 'mrp_paisa', 'cost_paisa', 'supplier_id'] as const).every(
      (k) => existing[k] === row[k],
    )
  ) {
    return { id, op: null }
  }
  return { id, op: { table: 'batches', op: 'upsert', row: { ...row } } }
}

/** A goods receipt with its lines and the stock movements it causes (one tx). */
export function receiveGoods(
  ctx: RowContext,
  input: {
    /** The receipt's id, when the caller needs it (the supplier's bill points at it). */
    receiptId?: string
    locationId: string
    /** The purchase order this delivery came against. */
    purchaseOrderId?: string | null
    supplierId: string | null
    reference: string | null
    receiptDate: string
    lines: {
      productId: string
      /** The order line this fills (only with `purchaseOrderId`). */
      purchaseOrderLineId?: string | null
      qtyMilli: number
      unitCostPaisa: number
      /** For batch-tracked products, with the batch as it is stored now (if any). */
      batch?: BatchInput & { existing: Row<typeof batches> | null }
      /** For serial-tracked products: one unit per serial, as stored now (if any). */
      serials?: { serial: string; existing: SerialRow | null }[]
    }[]
  },
): SyncOp[] {
  const receipt: WritableRow<typeof goodsReceipts> = {
    ...base(ctx),
    ...(input.receiptId ? { id: input.receiptId } : {}),
    purchase_order_id: input.purchaseOrderId ?? null,
    supplier_id: input.supplierId,
    location_id: input.locationId,
    receipt_date: input.receiptDate,
    reference: input.reference,
    notes: null,
  }
  const ops: SyncOp[] = [{ table: 'goods_receipts', op: 'upsert', row: receipt }]
  const batchOps = new Map<string, SyncOp>()
  input.lines.forEach((l, i) => {
    let lineBatch: string | null = null
    if (l.batch?.batchNo.trim()) {
      const { id, op } = batchOp(
        ctx,
        l.productId,
        {
          ...l.batch,
          costPaisa: l.batch.costPaisa ?? l.unitCostPaisa,
          supplierId: input.supplierId,
        },
        l.batch.existing,
      )
      lineBatch = id
      if (op && !batchOps.has(id)) {
        batchOps.set(id, op)
        ops.push(op)
      }
    }
    const line: WritableRow<typeof goodsReceiptLines> = {
      ...base(ctx),
      goods_receipt_id: receipt.id,
      purchase_order_line_id: (input.purchaseOrderId && l.purchaseOrderLineId) || null,
      line_no: i + 1,
      product_id: l.productId,
      qty_milli: l.qtyMilli,
      unit_cost_paisa: l.unitCostPaisa,
      batch_id: lineBatch,
    }
    ops.push({ table: 'goods_receipt_lines', op: 'upsert', row: line })
    for (const s of l.serials ?? []) {
      ops.push(
        serialOp(ctx, l.productId, s.serial, s.existing, {
          status: 'in_stock',
          location_id: input.locationId,
          receipt_line_id: line.id,
        }),
      )
    }
    ops.push({
      table: 'stock_movements',
      op: 'upsert',
      row: newMovement(ctx, {
        product_id: l.productId,
        location_id: input.locationId,
        type: 'receipt',
        qty_milli: l.qtyMilli,
        unit_cost_paisa: l.unitCostPaisa,
        ref_type: 'goods_receipt',
        ref_id: receipt.id,
        batch_id: lineBatch,
      }),
    })
  })
  return ops
}

export type SerialRow = Row<typeof productSerials>
export type SerialPatch = Partial<
  Pick<
    WritableRow<typeof productSerials>,
    | 'status'
    | 'location_id'
    | 'receipt_line_id'
    | 'sold_line_id'
    | 'returned_line_id'
    | 'warranty_until'
    | 'notes'
  >
>

/**
 * The upsert for one unit of a serial-tracked product (deterministic id, see
 * core serialId), merged into the unit as it is stored now.
 */
export function serialOp(
  ctx: RowContext,
  productId: string,
  serial: string,
  existing: SerialRow | null,
  patch: SerialPatch,
): SyncOp {
  const row: WritableRow<typeof productSerials> = {
    ...base(ctx),
    ...(existing ? { created_at: existing.created_at } : {}),
    id: serialId(ctx.tenantId, productId, serial),
    product_id: productId,
    serial: existing?.serial ?? normalizeSerial(serial),
    status: existing?.status ?? 'in_stock',
    location_id: existing?.location_id ?? null,
    receipt_line_id: existing?.receipt_line_id ?? null,
    sold_line_id: existing?.sold_line_id ?? null,
    returned_line_id: existing?.returned_line_id ?? null,
    warranty_until: existing?.warranty_until ?? null,
    notes: existing?.notes ?? null,
    ...patch,
  }
  return { table: 'product_serials', op: 'upsert', row: { ...row } }
}

/** One print of an issued bill (docs/plans/print-copies.md): its own tx, written after the paper. */
export function invoicePrintOp(
  ctx: RowContext,
  fields: Pick<WritableRow<typeof invoicePrints>, 'invoice_id' | 'format' | 'copy_no'>,
): SyncOp {
  const row: WritableRow<typeof invoicePrints> = {
    ...base(ctx),
    ...fields,
    printed_at: ctx.now ?? new Date().toISOString(),
  }
  return { table: 'invoice_prints', op: 'upsert', row: { ...row } }
}

export function insertOp(table: TableName, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

/** Upsert of an existing row with changes; server-set columns are dropped. */
export function updateOp<N extends TableName>(
  table: N,
  row: Row<(typeof tablesByName)[N]>,
  changes: Record<string, unknown>,
): SyncOp {
  const out: Record<string, unknown> = {}
  for (const [key, col] of Object.entries(tablesByName[table].columns)) {
    if (col.server) continue
    out[key] = (row as Record<string, unknown>)[key]
  }
  return { table, op: 'upsert', row: { ...out, ...changes, updated_at: new Date().toISOString() } }
}

export function deleteOp(table: TableName, id: string): SyncOp {
  return { table, op: 'delete', row: { id } }
}
